import dayjs from 'dayjs';

import { ToCronExpressionInput } from '@/shared/types/transform.types';

export const toCronExpression = (
  configuration: ToCronExpressionInput,
): string => {
  const { days, timeOfDay } = configuration;

  if (!days || !timeOfDay) {
    return 'cron(0 0 ? * * *)'; // Default cron expression
  }

  const [hours, minutes] = timeOfDay
    .split(':')
    .map((part) => parseInt(part, 10));

  const cronDays = days.sort((a, b) => a - b);
  const dayOfWeekExpression = cronDays.length > 0 ? cronDays.join(',') : '*';

  return `cron(${minutes} ${hours} ? * ${dayOfWeekExpression} *)`;
};

export const fromCronExpression = (
  cronExpression: string,
  expressionTimezone?: string,
): ToCronExpressionInput | null => {
  const cronRegex = /^cron\((\d+)\s+(\d+)\s+\?\s+\*\s+([1-7,*]+)\s+\*\)$/;
  const match = cronExpression.match(cronRegex);

  if (!match) {
    return null;
  }

  const [, minutesStr, hoursStr, daysOfWeekStr] = match;
  const originalHours = parseInt(hoursStr, 10);
  const originalMinutes = parseInt(minutesStr, 10);

  if (!expressionTimezone) {
    const timeOfDay = `${originalHours.toString().padStart(2, '0')}:${originalMinutes.toString().padStart(2, '0')}`;

    let days: number[] = [];
    if (daysOfWeekStr !== '*') {
      days = daysOfWeekStr
        .split(',')
        .map((day) => parseInt(day, 10))
        .sort((a, b) => a - b);
    }

    return { days, timeOfDay };
  }

  const userTimeZone = dayjs.tz.guess();

  // For each day of the week, calculate how it would be in the user's timezone
  let adjustedDays: number[] = [];
  const daysToProcess =
    daysOfWeekStr === '*'
      ? [1, 2, 3, 4, 5, 6, 7]
      : daysOfWeekStr.split(',').map((day) => parseInt(day, 10));

  for (const originalDay of daysToProcess) {
    // Create a reference date for the original day of the week
    // Use a specific week to avoid DST issues
    const referenceDate = dayjs()
      .startOf('week')
      .add(originalDay - 1, 'day');

    // Create the time in the source timezone
    const sourceTime = referenceDate
      .tz(expressionTimezone)
      .hour(originalHours)
      .minute(originalMinutes)
      .second(0)
      .millisecond(0);

    // Convert to user's timezone
    const userTime = sourceTime.tz(userTimeZone);

    // Calculate the new day of the week (1-7, where 1 = Sunday)
    const newDayOfWeek = userTime.day() + 1;

    if (!adjustedDays.includes(newDayOfWeek)) {
      adjustedDays.push(newDayOfWeek);
    }
  }

  // Use the first day to calculate the final time
  const firstDay = daysToProcess[0];
  const referenceDate = dayjs().startOf('week').add(firstDay, 'day');

  const sourceTime = referenceDate
    .tz(expressionTimezone)
    .hour(originalHours)
    .minute(originalMinutes)
    .second(0)
    .millisecond(0);

  const userTime = sourceTime.tz(userTimeZone);

  const timeOfDay = `${userTime.hour().toString().padStart(2, '0')}:${userTime.minute().toString().padStart(2, '0')}`;

  // If it was '*', represent empty array
  const finalDays =
    daysOfWeekStr === '*' ? [] : adjustedDays.sort((a, b) => a - b);

  return {
    days: finalDays,
    timeOfDay,
  };
};

export const snakeToCamelCase = <T>(obj: T): T => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  return Object.keys(obj).reduce<Record<string, any>>((result, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );

    const _key = key as keyof T;

    const value =
      typeof obj[_key] === 'object' && obj[_key] !== null
        ? snakeToCamelCase(obj[_key])
        : obj[_key];

    result[camelKey] = value;
    return result;
  }, {}) as T;
};
